
import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
  onSnapshot
} from '../firebase';
import { db } from '../firebase';
import { Product, Store, Ad } from '../types';

export const useProducts = (categoryId?: number) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const productsRef = collection(db, 'products');
        const constraints = [where('isActive', '==', true)];

        if (categoryId) {
          constraints.push(where('categoryId', '==', categoryId));
        }

        // Try to order by createdAt if index exists, otherwise fall back to simple query
        // constraints.push(orderBy('createdAt', 'desc')); 

        const q = query(productsRef, ...constraints);
        const snapshot = await getDocs(q);
        const fetched = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), docId: doc.id } as unknown as Product));
        setProducts(fetched);
      } catch (err: any) {
        console.error("Error fetching products:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [categoryId]);

  return { products, loading, error };
};

export const useShops = () => {
  const [shops, setShops] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchShops = async () => {
      setLoading(true);
      try {
        const shopsRef = collection(db, 'stores');
        // Assuming stores are active by default if isActive is undefined, or filter explicitly
        // const q = query(shopsRef, where('isActive', '==', true), orderBy('createdAt', 'desc'));
        const q = query(shopsRef); // Simplified for stability without indexes
        const snapshot = await getDocs(q);
        const fetched = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), docId: doc.id } as unknown as Store));
        setShops(fetched);
      } catch (err: any) {
        console.error("Error fetching shops:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchShops();
  }, []);

  return { shops, loading, error };
};

export const useShopProducts = (shopId: string | number) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!shopId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const productsRef = collection(db, 'products');

    // We listen to all products for this shop and filter by multiple possible field names/values
    const q = query(
      productsRef,
      where('isActive', '==', true)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allFetched = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), docId: doc.id } as any));

      // Comprehensive filtering
      const filtered = allFetched.filter(p => {
        const pStoreId = p.storeId?.toString();
        const pShopId = p.shopId?.toString();
        const targetId = shopId.toString();

        return pStoreId === targetId || pShopId === targetId;
      });

      setProducts(filtered);
      setLoading(false);
    }, (err) => {
      console.error("Error fetching shop products:", err);
      setError(err.message);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [shopId]);

  return { products, loading, error };
};

export const useProduct = (productId: string | number) => {
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!productId) return;

    const fetchProduct = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, 'products', productId.toString());
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setProduct({ id: docSnap.id, ...docSnap.data() } as unknown as Product);
        } else {
          setError("Product not found");
        }
      } catch (err: any) {
        console.error("Error fetching product:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  return { product, loading, error };
};

export const useHomepage = () => {
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [randomProducts, setRandomProducts] = useState<Product[]>([]);
  const [featuredShops, setFeaturedShops] = useState<Store[]>([]);
  const [ads, setAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);

    // 1. Ads Snapshot
    const unsubAds = onSnapshot(collection(db, 'ads'), (snap) => {
      setAds(snap.docs.map(doc => ({ id: doc.id, ...doc.data(), docId: doc.id } as unknown as Ad)));
    }, (err) => setError(err.message));

    // 2. Featured Products Snapshot
    const unsubFeatured = onSnapshot(query(collection(db, 'products'), where('isFeatured', '==', true), limit(10)), (snap) => {
      setFeaturedProducts(snap.docs.map(doc => ({ id: doc.id, ...doc.data(), docId: doc.id } as unknown as Product)));
    }, (err) => setError(err.message));

    // 3. Featured/Active Shops Snapshot
    const unsubShops = onSnapshot(query(collection(db, 'stores'), limit(8)), (snap) => {
      setFeaturedShops(snap.docs.map(doc => ({ id: doc.id, ...doc.data(), docId: doc.id } as unknown as Store)));
    }, (err) => setError(err.message));

    // 4. Random Products Snapshot
    const unsubRandom = onSnapshot(query(collection(db, 'products'), limit(50)), (snap) => {
      let allRandom = snap.docs.map(doc => ({ id: doc.id, ...doc.data(), docId: doc.id } as unknown as Product));
      // Client-side shuffle
      allRandom = allRandom.sort(() => 0.5 - Math.random()).slice(0, 12);
      setRandomProducts(allRandom);
      setLoading(false);
    }, (err) => {
      setError(err.message);
      setLoading(false);
    });

    return () => {
      unsubAds();
      unsubFeatured();
      unsubShops();
      unsubRandom();
    };
  }, []);

  return { featuredProducts, randomProducts, featuredShops, ads, loading, error };
};
